import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FileText, Download, Calendar, Euro, Clock, User, Briefcase, CalendarDays, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_FR = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
  thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
};

const InfoRow = ({ label, value, mono, highlight }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span className={cn(
        "text-sm font-semibold",
        mono && "font-mono",
        highlight && "text-lg"
      )} style={highlight ? { color: 'var(--primary-400)' } : { color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
};

const ContractDetailsModal = ({ isOpen, onClose, contract }) => {
  if (!contract) return null;
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contrat_${contract.child_name.replace(' ', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement du PDF');
    }
  };

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return String(d); }
  };

  const contractTypeLabel = contract.contract_type === 'cdd' ? 'CDD' : 'CDI';
  const yearLabel = contract.year_type === 'incomplete'
    ? `Incomplète (${contract.weeks_per_year || '?'} sem.)`
    : 'Complète (52 sem.)';
  const leaveLabel = contract.leave_mode === 'ouvres' ? 'Jours ouvrés' : 'Jours ouvrables';
  const leavePayLabel = contract.leave_payment_mode === 'dixieme' ? '1/10ème' : 'Maintien de salaire';

  const statusBadge = (s) => {
    if (s === 'active') return <Badge variant="outline" className="bg-[var(--success-light)] text-[var(--success)] border-[var(--success)]">Actif</Badge>;
    if (s === 'terminated') return <Badge variant="outline" className="bg-[var(--warning-light)] text-[var(--warning-dark)] border-[var(--warning)]">Résilié</Badge>;
    return <Badge variant="outline" className="bg-[var(--neutral-100)] text-[var(--text-muted)] border-[var(--neutral-300)]">Inactif</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[var(--bg-primary)] max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="contract-details-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-400)' }}>
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Détails du Contrat
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs border-[var(--primary-400)] text-[var(--primary-400)]">
                    {contractTypeLabel}
                  </Badge>
                  {statusBadge(contract.status)}
                </div>
              </div>
            </div>
            <Button
              onClick={handleDownloadPDF}
              style={{ backgroundColor: 'var(--secondary-400)' }}
              data-testid="download-pdf-button"
              size="sm"
            >
              <Download className="mr-2 h-4 w-4" />
              PDF
            </Button>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 mt-3">
          {/* Child Info */}
          <Card className="p-4 bg-[var(--bg-tertiary)]">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--primary-400)' }}>
              <User className="h-4 w-4" /> Enfant
            </h3>
            <div className="space-y-1">
              <InfoRow label="Prénom" value={contract.child_first_name || contract.child_name} />
              {contract.child_first_name && <InfoRow label="Nom" value={contract.child_name} />}
              <InfoRow label="Date de naissance" value={fmtDate(contract.child_birth_date)} />
              <InfoRow label="Sexe" value={contract.child_sex === 'M' ? 'Garçon' : contract.child_sex === 'F' ? 'Fille' : null} />
            </div>
          </Card>
          
          {/* Parent / Nanny */}
          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <User className="h-4 w-4" /> Intervenants
            </h3>
            <div className="space-y-1">
              {contract.parent_info && <InfoRow label="Parent" value={contract.parent_info.full_name} />}
              {contract.nanny_info ? (
                <InfoRow label="Nounou" value={contract.nanny_info.full_name} />
              ) : (
                <div className="flex justify-between items-center py-1">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Nounou</span>
                  <Badge variant="outline" className="text-xs bg-[var(--warning-light)] text-[var(--warning-dark)] border-[var(--warning)]">
                    Non liée
                  </Badge>
                </div>
              )}
            </div>
          </Card>
          
          {/* Contract Structure & Dates */}
          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Briefcase className="h-4 w-4" /> Contrat
            </h3>
            <div className="space-y-1">
              <InfoRow label="Type" value={contractTypeLabel} />
              <InfoRow label="Année" value={yearLabel} />
              {contract.hours_per_week > 0 && <InfoRow label="Heures / semaine" value={`${contract.hours_per_week}h`} mono />}
              <Separator className="my-2" />
              <InfoRow label="Début" value={fmtDate(contract.start_date)} />
              {contract.end_date && <InfoRow label="Fin" value={fmtDate(contract.end_date)} />}
              {contract.signature_date && <InfoRow label="Signature" value={fmtDate(contract.signature_date)} />}
              {contract.trial_period_weeks > 0 && (
                <InfoRow label="Période d'essai" value={`${contract.trial_period_weeks} semaines`} />
              )}
              <InfoRow label="Préavis" value={`${contract.notice_period_months || 1} mois`} />
            </div>
          </Card>

          {/* Rémunération */}
          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Euro className="h-4 w-4" /> Rémunération & Indemnités
            </h3>
            <div className="space-y-1">
              <InfoRow label="Taux horaire net" value={`${contract.hourly_rate}€/h`} mono highlight />
              {contract.meal_price > 0 && <InfoRow label="Repas" value={`${contract.meal_price.toFixed(2)}€/jour`} mono />}
              {contract.snack_price > 0 && <InfoRow label="Goûter" value={`${contract.snack_price.toFixed(2)}€/jour`} mono />}
              {contract.daily_allowance > 0 && <InfoRow label="Entretien" value={`${contract.daily_allowance.toFixed(2)}€/jour`} mono />}
              {contract.km_allowance > 0 && <InfoRow label="Kilométrique" value={`${contract.km_allowance.toFixed(2)}€/jour`} mono />}
            </div>
          </Card>
          
          {/* Schedule */}
          {contract.working_days && contract.working_days.length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Clock className="h-4 w-4" /> Planning
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {contract.working_days.map(day => {
                  const schedule = contract.daily_schedule?.[day] || {};
                  return (
                    <div key={day} className="p-2.5 rounded-lg border-l-3 border-[var(--primary-400)]" style={{ backgroundColor: 'var(--bg-secondary)', borderLeftWidth: '3px', borderLeftColor: 'var(--primary-400)' }}>
                      <div className="font-semibold text-xs" style={{ color: 'var(--primary-400)' }}>{DAYS_FR[day]}</div>
                      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {schedule.start || '08:00'} - {schedule.end || '18:00'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Congés & Options */}
          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <CalendarDays className="h-4 w-4" /> Congés & Options
            </h3>
            <div className="space-y-1">
              <InfoRow label="Mode acquisition" value={leaveLabel} />
              <InfoRow label="Mode paiement" value={leavePayLabel} />
              <Separator className="my-2" />
              <div className="flex justify-between items-center py-1">
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Jours fériés</span>
                <Badge variant="outline" className={cn(
                  "text-xs",
                  contract.work_on_holidays 
                    ? "bg-[var(--warning-light)] text-[var(--warning-dark)] border-[var(--warning)]" 
                    : "bg-[var(--neutral-100)] text-[var(--text-muted)] border-[var(--neutral-300)]"
                )}>
                  {contract.work_on_holidays ? 'Travaillés' : 'Chômés'}
                </Badge>
              </div>
            </div>
          </Card>
          
          {/* Notes */}
          {contract.schedule_notes && (
            <Card className="p-4">
              <h3 className="font-semibold text-sm mb-2">Notes</h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{contract.schedule_notes}</p>
            </Card>
          )}
          
          {/* Link Code */}
          {!contract.nanny_user_id && contract.link_code && (
            <Card className="p-5" style={{ backgroundColor: 'var(--primary-100)', borderColor: 'var(--primary-400)' }}>
              <div className="text-center space-y-2">
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Code de Liaison</p>
                <div className="text-3xl font-mono font-bold" style={{ color: 'var(--primary-400)', letterSpacing: '0.3em' }}>
                  {contract.link_code}
                </div>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  Communiquez ce code à votre assistante maternelle pour lier le contrat
                </p>
              </div>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContractDetailsModal;
